import React, { Component } from "react";
import Button from "../common/Button";

const CROP_WIDTH = 300;
const CROP_HEIGHT = 200;

class OfferImageCropper extends Component {
  constructor(props) {
    super(props);
    this.state = {
      naturalWidth: 0,
      naturalHeight: 0,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      dragging: false,
    };
    this.dragStart = null;
    this.imageRef = React.createRef();
  }

  componentDidMount() {
    this.loadImage(this.props.imageSrc);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.imageSrc !== this.props.imageSrc) {
      this.loadImage(this.props.imageSrc);
    }
  }

  loadImage = (src) => {
    if (!src) return;
    const image = new Image();
    image.onload = () => {
      const naturalWidth = image.naturalWidth || image.width;
      const naturalHeight = image.naturalHeight || image.height;
      const baseScale = Math.max(
        CROP_WIDTH / naturalWidth,
        CROP_HEIGHT / naturalHeight
      );
      const displayW = naturalWidth * baseScale;
      const displayH = naturalHeight * baseScale;
      this.setState({
        naturalWidth,
        naturalHeight,
        zoom: 1,
        offsetX: (CROP_WIDTH - displayW) / 2,
        offsetY: (CROP_HEIGHT - displayH) / 2,
        dragging: false,
      });
    };
    image.src = src;
  };

  getBaseScale = () => {
    const { naturalWidth, naturalHeight } = this.state;
    if (!naturalWidth || !naturalHeight) return 1;
    return Math.max(CROP_WIDTH / naturalWidth, CROP_HEIGHT / naturalHeight);
  };

  getDisplaySize = () => {
    const { naturalWidth, naturalHeight, zoom } = this.state;
    const scale = this.getBaseScale() * zoom;
    return {
      width: naturalWidth * scale,
      height: naturalHeight * scale,
      scale,
    };
  };

  clampOffset = (offsetX, offsetY, width, height) => {
    const minX = Math.min(0, CROP_WIDTH - width);
    const maxX = Math.max(0, CROP_WIDTH - width);
    const minY = Math.min(0, CROP_HEIGHT - height);
    const maxY = Math.max(0, CROP_HEIGHT - height);
    return {
      offsetX: Math.min(maxX, Math.max(minX, offsetX)),
      offsetY: Math.min(maxY, Math.max(minY, offsetY)),
    };
  };

  handlePointerDown = (event) => {
    event.preventDefault();
    this.dragStart = {
      x: event.clientX,
      y: event.clientY,
      offsetX: this.state.offsetX,
      offsetY: this.state.offsetY,
    };
    this.setState({ dragging: true });
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  handlePointerMove = (event) => {
    if (!this.dragStart) return;
    const { width, height } = this.getDisplaySize();
    const next = this.clampOffset(
      this.dragStart.offsetX + (event.clientX - this.dragStart.x),
      this.dragStart.offsetY + (event.clientY - this.dragStart.y),
      width,
      height
    );
    this.setState(next);
  };

  handlePointerUp = () => {
    this.dragStart = null;
    this.setState({ dragging: false });
  };

  handleZoomChange = (event) => {
    const zoom = Number(event.target.value);
    const { naturalWidth, naturalHeight, offsetX, offsetY } = this.state;
    const oldScale = this.getBaseScale() * this.state.zoom;
    const newScale = this.getBaseScale() * zoom;
    const centerX = CROP_WIDTH / 2;
    const centerY = CROP_HEIGHT / 2;
    const imageCenterX = (centerX - offsetX) / oldScale;
    const imageCenterY = (centerY - offsetY) / oldScale;
    const nextOffsetX = centerX - imageCenterX * newScale;
    const nextOffsetY = centerY - imageCenterY * newScale;
    const width = naturalWidth * newScale;
    const height = naturalHeight * newScale;
    const clamped = this.clampOffset(nextOffsetX, nextOffsetY, width, height);
    this.setState({ zoom, ...clamped });
  };

  handleApply = () => {
    const { imageSrc, contentType, onApply } = this.props;
    const { offsetX, offsetY } = this.state;
    const { scale } = this.getDisplaySize();
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = CROP_WIDTH;
      canvas.height = CROP_HEIGHT;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, CROP_WIDTH, CROP_HEIGHT);
      const srcX = -offsetX / scale;
      const srcY = -offsetY / scale;
      const srcW = CROP_WIDTH / scale;
      const srcH = CROP_HEIGHT / scale;
      ctx.drawImage(image, srcX, srcY, srcW, srcH, 0, 0, CROP_WIDTH, CROP_HEIGHT);
      const outputType = contentType?.includes("png") ? "image/png" : "image/jpeg";
      onApply(canvas.toDataURL(outputType, 0.92));
    };
    image.src = imageSrc;
  };

  render() {
    const { imageSrc, onCancel } = this.props;
    const { offsetX, offsetY, zoom, dragging, naturalWidth } = this.state;
    const { width, height } = this.getDisplaySize();

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1300,
          background: "rgba(0, 0, 0, 0.72)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: "#fff",
            borderRadius: 8,
            padding: 16,
            boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
          }}
        >
          <h3 style={{ margin: "0 0 4px", fontSize: 18 }}>Edit photo</h3>
          <p style={{ margin: "0 0 12px", color: "#667085", fontSize: 13 }}>
            Drag to reposition · use zoom · frame is 300 × 200
          </p>

          <div
            style={{
              width: CROP_WIDTH,
              height: CROP_HEIGHT,
              margin: "0 auto",
              overflow: "hidden",
              position: "relative",
              background: "#111827",
              borderRadius: 4,
              cursor: dragging ? "grabbing" : "grab",
              touchAction: "none",
              userSelect: "none",
            }}
            onPointerDown={this.handlePointerDown}
            onPointerMove={this.handlePointerMove}
            onPointerUp={this.handlePointerUp}
            onPointerCancel={this.handlePointerUp}
          >
            {naturalWidth > 0 && (
              <img
                ref={this.imageRef}
                src={imageSrc}
                alt="Crop"
                draggable={false}
                style={{
                  position: "absolute",
                  left: offsetX,
                  top: offsetY,
                  width,
                  height,
                  maxWidth: "none",
                  pointerEvents: "none",
                }}
              />
            )}
            <div
              style={{
                position: "absolute",
                inset: 0,
                boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.85)",
                pointerEvents: "none",
              }}
            />
          </div>

          <div style={{ marginTop: 14, padding: "0 4px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 13,
                color: "#344054",
              }}
            >
              Zoom
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                onChange={this.handleZoomChange}
                style={{ flex: 1 }}
              />
            </label>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 16,
            }}
          >
            <Button variant="outlined" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={this.handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default OfferImageCropper;
export { CROP_WIDTH, CROP_HEIGHT };
